import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import {
  useGuardrailRuleLabels,
  type RuleLabel,
} from "src/components/guardrailRuleLabels";

import { workforceGuardrailInbox } from "./workforceGuardrailInbox.graphql";
import {
  labelInboxRequisition,
  sortInboxItems,
  toInboxItem,
  type InboxItem,
  type QueueRow,
} from "./inboxItem";

type InboxQuery = {
  workforceGuardrailQueue?: {
    waitingCount?: number | null;
    requisitions?: QueueRow[] | null;
  } | null;
};

const labelRow = (row: QueueRow, rules: RuleLabel[]): QueueRow => {
  const requisition = row.requisition;
  if (!requisition) {
    return row;
  }
  return { ...row, requisition: labelInboxRequisition(requisition, rules) };
};

export const useInboxItems = (
  enabled: boolean
): {
  items: InboxItem[];
  waitingCount: number;
  loading: boolean;
  error: unknown;
  rules: RuleLabel[];
} => {
  const { data, loading, error } = useQuery<InboxQuery>(
    workforceGuardrailInbox,
    {
      skip: !enabled,
    }
  );
  const labels = useGuardrailRuleLabels(enabled);
  const items = useMemo(() => {
    if (!labels.ready) {
      return [];
    }
    return sortInboxItems(
      (data?.workforceGuardrailQueue?.requisitions ?? []).map((row) =>
        toInboxItem(labelRow(row, labels.rules))
      )
    );
  }, [data, labels.ready, labels.rules]);
  return {
    items,
    waitingCount: data?.workforceGuardrailQueue?.waitingCount ?? 0,
    loading: loading || (enabled && !labels.ready),
    error,
    rules: labels.rules,
  };
};
